/**
 * Public Video Viewer Route
 *
 * Displays a publicly shared video via share token.
 * No authentication required.
 * Full-screen presentation mode with playback controls.
 *
 * Uses VideoCompositionPlayer for multi-slide video playback.
 */

import { createFileRoute, notFound } from '@tanstack/react-router';
import { useEffect, useState, useRef, useCallback, useMemo, type Ref } from 'react';
import { Button } from '../components/ui/button';
import { Loader2, Minimize2, Copy, Check, Play } from 'lucide-react';
import { cn } from '../lib/utils';
import type { VideoManifest, VideoPlaybackState } from '../types/videoManifest';
import { VideoCompositionPlayer, type VideoCompositionPlayerRef } from '../components/video-workspace/VideoCompositionPlayer';
import { VisibilityPauseOverlay } from '../components/video-workspace/VisibilityPauseOverlay';
import { GsapPlayerControls } from '../components/slides/GsapPlayerControls';
import { SegmentedProgressBar } from '../components/slides/SegmentedProgressBar';
import { DEFAULT_SLIDE_RESOLUTION } from '../config/gsapViewer';
import { SlidesModeControls, type PlayerMode } from '../components/player';
import { useAutoHideControls } from '../hooks/useAutoHideControls';
import { useSwipeGesture } from '../hooks/useSwipeGesture';
import { useTouchDevice } from '../hooks/useTouchDevice';
import { useContainerSize } from '../hooks/useContainerSize';
import { useViewportOrientation } from '../hooks/useViewportOrientation';
import { RotateDeviceHint } from '../components/player/RotateDeviceHint';
import { useScrollVisibility } from '../hooks/useScrollVisibility';
import { useVideoAnalytics } from '../hooks/useVideoAnalytics';
import { ReviewModeOverlay } from '../components/video-share/review/ReviewModeOverlay';
import { createEmbeddedPlayer, type EmbeddedPlayer } from '../components/video-share/review/embeddedPlayerRef';
import { playerUrl } from '../lib/vplay';
import { ImmersiveFullscreen } from '../lib/immersiveFullscreen';
import { getAuthToken } from '../services/firebase';
import { authenticatedFetch } from '../services/authApi';

// API base URL from environment
const API_BASE_URL = import.meta.env.VITE_API_URL || '';

interface VideoInfo {
  title: string | null;
  slideCount: number;
  totalDuration: number;
  thumbnailUrl: string | null;
}

interface PublicVideoResponse {
  success: boolean;
  videoInfo: VideoInfo;
  author: string | null;
  /** Whether reviewer comments via ?review=true are enabled on this share link. */
  allowReviews?: boolean;
  /** Full CDN URL of the baked static player bundle, or null if not baked. */
  staticBundleUrl?: string | null;
}

export const Route = createFileRoute('/video/$shareToken')({
  component: PublicVideoViewer,
  validateSearch: (search: Record<string, unknown>) => {
    // ?review=true | ?review=1 puts the public viewer into reviewer mode.
    const raw = search.review;
    const review = raw === true || raw === 1 || raw === 'true' || raw === '1';
    return { review };
  },
  loader: async ({ params }) => {
    const { shareToken } = params;

    if (!shareToken) {
      throw notFound();
    }

    try {
      // Fetch public video info from API (validates the token)
      const response = await fetch(`${API_BASE_URL}/api/video-share/${shareToken}`);

      if (!response.ok) {
        // No public page: it may be a private video, which plays for signed-in members of its organization.
        if (response.status === 404) {
          return { shareToken, private: true as const };
        }
        throw new Error('Failed to load video');
      }

      const data: PublicVideoResponse = await response.json();

      if (!data.success) {
        throw notFound();
      }

      // Return the share token and video info
      // The player will fetch the actual manifest from the public API endpoint
      return {
        shareToken,
        videoInfo: data.videoInfo,
        author: data.author,
        allowReviews: !!data.allowReviews,
        staticBundleUrl: data.staticBundleUrl ?? null,
        manifestUrl: `${API_BASE_URL}/api/video-share/${shareToken}/manifest.json`,
      };
    } catch (error) {
      console.error('Error loading public video:', error);
      throw notFound();
    }
  },
  notFoundComponent: () => (
    <div className="min-h-screen bg-black flex items-center justify-center">
      <div className="text-center space-y-4">
        <div className="text-6xl">🔒</div>
        <h1 className="text-2xl font-bold text-white">Video Not Found</h1>
        <p className="text-white/60 max-w-md">
          This share link may have expired, been revoked, or doesn't exist.
        </p>
      </div>
    </div>
  ),
});

/**
 * Router: serve the fast edge-baked player when the bundle exists (resolved
 * server-side — the browser can't reliably probe the CDN cross-origin), otherwise
 * the legacy inline player. Review mode on a baked share is the same iframe with
 * the review overlay on top, driven through the player's postMessage embed
 * contract. This keeps the branded app.4klabs.ai/video URL — the speedup is invisible.
 */
type LoaderData = ReturnType<typeof Route.useLoaderData>;
type PublicLoaderData = Exclude<LoaderData, { private: true }>;

/** The loader data of a public share: the only kind the views below are rendered for (PublicVideoViewer routes a private one away). */
function usePublicLoaderData(): PublicLoaderData {
  const data = Route.useLoaderData();
  if ('private' in data) throw new Error('a private video has no public share data');
  return data;
}

function PublicVideoViewer() {
  const data = Route.useLoaderData();
  const { review: reviewParam } = Route.useSearch();
  if ('private' in data) return <PrivateVideoView shareToken={data.shareToken} />;
  const { shareToken, videoInfo, author, allowReviews, staticBundleUrl, manifestUrl } = data;
  const reviewMode = reviewParam && allowReviews;

  if (staticBundleUrl && reviewMode) {
    return <FastReviewView shareToken={shareToken} videoInfo={videoInfo} author={author} manifestUrl={manifestUrl} staticBundleUrl={staticBundleUrl} />;
  }
  if (staticBundleUrl) {
    return <FastShareView shareToken={shareToken} videoInfo={videoInfo} author={author} staticBundleUrl={staticBundleUrl} />;
  }
  return <LegacyShareViewer />;
}

type PrivateState =
  | { kind: 'loading' }
  | { kind: 'ready'; url: string; title: string | null }
  | { kind: 'denied' | 'preparing' | 'missing' };

const PRIVATE_MESSAGES: Record<'denied' | 'preparing' | 'missing', { title: string; body: string }> = {
  denied: { title: 'This video is private', body: "It is shared with its organization only, and your account is not a member. Ask its owner to add you." },
  preparing: { title: 'Almost ready', body: 'This video is still being prepared. Try again in a few minutes.' },
  missing: { title: 'Video Not Found', body: "This share link may have expired, been revoked, or doesn't exist." },
};

const signIn = (shareToken: string) => {
  window.location.href = `/login?redirect=${encodeURIComponent(`/video/${shareToken}${window.location.search}`)}`;
};

/**
 * A private video: no public page exists, so the viewer signs in, and the API mints a viewing link
 * when they are its owner or a member of its organization. It plays in the same frame as a public share.
 */
function PrivateVideoView({ shareToken }: { shareToken: string }) {
  const [state, setState] = useState<PrivateState>({ kind: 'loading' });
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!(await getAuthToken())) { signIn(shareToken); return; }
      // authenticatedFetch prefixes the API base, which already ends in /api.
      const response = await authenticatedFetch(`/video-share/${shareToken}/viewing`);
      if (cancelled) return;
      if (response.status === 401) { signIn(shareToken); return; }
      if (!response.ok) { setState({ kind: response.status === 403 ? 'denied' : response.status === 409 ? 'preparing' : 'missing' }); return; }
      const body = await response.json();
      setState({ kind: 'ready', url: body.url, title: body.title ?? null });
    })().catch(() => { if (!cancelled) setState({ kind: 'missing' }); });
    return () => { cancelled = true; };
  }, [shareToken]);

  if (state.kind === 'ready') return <FastShareView shareToken={shareToken} staticBundleUrl={state.url} videoInfo={{ title: state.title }} />;
  if (state.kind === 'loading') {
    return <div className="min-h-screen bg-black flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-white/60" /></div>;
  }
  const message = PRIVATE_MESSAGES[state.kind];
  return (
    <div className="min-h-screen bg-black flex items-center justify-center">
      <div className="text-center space-y-4 px-6">
        <div className="text-6xl">🔒</div>
        <h1 className="text-2xl font-bold text-white">{message.title}</h1>
        <p className="text-white/60 max-w-md">{message.body}</p>
      </div>
    </div>
  );
}

/**
 * Review mode over the baked player. Titles come from the frozen share manifest; the
 * position comes from the player. The overlay's `c` shortcut only fires while focus is
 * on this page (keys typed into the iframe stay there) — the Add comment pill always works.
 */
function FastReviewView({ shareToken, videoInfo, author, manifestUrl, staticBundleUrl }: {
  shareToken: string;
  staticBundleUrl: string;
  videoInfo: VideoInfo;
  author?: string | null;
  manifestUrl: string;
}) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const playerRef = useRef<EmbeddedPlayer | null>(null);
  const [playbackState, setPlaybackState] = useState(() => initialPlaybackState(videoInfo.totalDuration));
  const [manifest, setManifest] = useState<VideoManifest | null>(null);

  useEffect(() => {
    const frame = iframeRef.current;
    if (!frame) return;
    const player = createEmbeddedPlayer({
      frame,
      initialState: initialPlaybackState(videoInfo.totalDuration),
      onState: setPlaybackState,
    });
    playerRef.current = player;
    return () => {
      player.destroy();
      playerRef.current = null;
    };
  }, [videoInfo.totalDuration]);

  useEffect(() => {
    let cancelled = false;
    loadShareManifest(shareToken, manifestUrl)
      .then(({ manifest: data }) => { if (!cancelled) setManifest(data); })
      .catch((err) => console.error('[VideoShare] review manifest failed:', err));
    return () => { cancelled = true; };
  }, [shareToken, manifestUrl]);

  return (
    <>
      <FastShareView shareToken={shareToken} videoInfo={videoInfo} author={author} staticBundleUrl={staticBundleUrl} iframeRef={iframeRef} />
      <ReviewModeOverlay
        shareToken={shareToken}
        manifest={manifest}
        playerRef={playerRef}
        playbackState={playbackState}
        videoTitle={videoInfo.title}
      />
    </>
  );
}

function initialPlaybackState(totalDuration: number): VideoPlaybackState {
  return {
    isPlaying: false,
    currentTime: 0,
    totalDuration: totalDuration || 0,
    currentSlideId: '',
    currentSlideIndex: 0,
    currentSlideTime: 0,
    currentSlideDuration: 0,
    playbackRate: 1,
    isContinuousMode: true,
    isTransitioning: false,
  };
}

/**
 * Mint a short-lived, share-scoped session token, then fetch the frozen manifest with it.
 * (In dual-accept rollout the backend still serves without the token, so a failed mint
 * degrades gracefully.)
 */
async function loadShareManifest(shareToken: string, manifestUrl: string): Promise<{ manifest: VideoManifest; sessionToken?: string }> {
  let sessionToken: string | undefined;
  try {
    const sessionRes = await fetch(`${API_BASE_URL}/api/video-share/${shareToken}/session`, { method: 'POST' });
    if (sessionRes.ok) sessionToken = (await sessionRes.json()).token;
  } catch (e) {
    console.warn('[VideoShare] session mint failed, continuing:', e);
  }
  const response = await fetch(
    manifestUrl,
    sessionToken ? { headers: { Authorization: `Bearer ${sessionToken}` } } : undefined
  );
  if (!response.ok) {
    throw new Error('Failed to load video manifest');
  }
  return { manifest: await response.json(), sessionToken };
}

/** Fast path: the baked player in an iframe, wrapped in the share-page chrome. */
function FastShareView({ shareToken, videoInfo, author, staticBundleUrl, iframeRef }: {
  shareToken: string;
  staticBundleUrl?: string | null;
  videoInfo: { title?: string | null };
  author?: string | null;
  iframeRef?: Ref<HTMLIFrameElement>;
}) {
  const [copied, setCopied] = useState(false);
  const [compact, setCompact] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
  useEffect(() => {
    const onResize = () => setCompact(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const copy = useCallback(async () => {
    try { await navigator.clipboard.writeText(window.location.href); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch { /* noop */ }
  }, []);
  const title = videoInfo.title || 'Untitled Video';

  return (
    <div className="bg-black flex flex-col overflow-hidden relative h-[100dvh]">
      {!compact && (
        <div className="flex-shrink-0 bg-black/80 backdrop-blur-sm py-3 px-6 border-b border-white/10">
          <div className="flex items-center justify-between max-w-7xl mx-auto gap-2">
            <h1 className="text-white font-medium truncate text-base min-w-0 flex-1">{title}</h1>
            <Button onClick={copy} variant="ghost" size="sm" className="text-white/70 hover:text-white hover:bg-white/10">
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}<span className="ml-2">{copied ? 'Copied!' : 'Share'}</span>
            </Button>
          </div>
        </div>
      )}
      <div className="flex-1 min-h-0 relative">
        <iframe ref={iframeRef} title={title} src={playerUrl(shareToken, staticBundleUrl, window.location.search)} className="w-full h-full border-0" allow="autoplay; fullscreen" allowFullScreen />
        {compact && (
          <div className="absolute top-0 left-0 right-0 z-30 bg-gradient-to-b from-black/70 to-transparent px-3 pt-2 pb-8 pr-16 pointer-events-none">
            <div className="flex items-center gap-2 pointer-events-auto">
              <h1 className="text-white text-sm font-medium truncate min-w-0 flex-1">{title}</h1>
              <Button onClick={copy} variant="ghost" size="sm" className="text-white/70 hover:text-white hover:bg-white/10 flex-shrink-0 h-8 w-8 p-0">
                {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
              </Button>
            </div>
          </div>
        )}
      </div>
      {!compact && (
        <div className="flex-shrink-0 bg-black py-2 px-6 border-t border-white/10">
          <div className="flex items-center justify-between max-w-7xl mx-auto">
            <p className="text-white/40 text-xs">Powered by <span className="text-white/60">4K Labs</span></p>
            {author && <p className="text-white/40 text-xs">Created by <span className="text-white/60">{author}</span></p>}
          </div>
        </div>
      )}
    </div>
  );
}

function LegacyShareViewer() {
  const { shareToken, videoInfo, author, manifestUrl, allowReviews } = usePublicLoaderData();
  const { review: reviewParam } = Route.useSearch();
  // ?review=true is honored only when the share link has allowReviews=true on the server.
  // Without this gate, anyone with a public share link could comment.
  const reviewMode = reviewParam && allowReviews;

  const containerRef = useRef<HTMLDivElement>(null);
  const playerAreaRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<VideoCompositionPlayerRef>(null);

  const { compact } = useContainerSize(containerRef);
  const isTouchDevice = useTouchDevice();

  const [isFullscreen, setIsFullscreen] = useState(false);
  const [scale, setScale] = useState(0.5);
  const [manifest, setManifest] = useState<VideoManifest | null>(null);
  // Short-lived session token gating manifest + slide fetches (minted on load).
  const [sessionToken, setSessionToken] = useState<string | undefined>(undefined);

  // Derive resolution from manifest metadata (supports 16:9, 1:1, 9:16)
  const resolution = manifest?.metadata?.resolution ?? DEFAULT_SLIDE_RESOLUTION;
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // GA4 video analytics
  const analytics = useVideoAnalytics({
    shareToken,
    manifest,
    isEmbed: false,
  });

  // Player mode: 'video' (auto-play) or 'slides' (manual navigation).
  // Slides mode is currently UNPLUGGED — we lock to 'video'. The slides-mode handlers,
  // SlidesModeControls, and PlayerModeToggle code remain in the tree but are never rendered
  // or reachable, so re-enabling later is a one-line change (restore the localStorage init
  // and re-mount the toggle). setPlayerMode is kept for that future path.
  // Locked to 'video' (slides mode unplugged). useState keeps the union type so the dormant
  // slides-mode branches still type-check; we simply never provide a setter to flip it.
  const [playerMode] = useState<PlayerMode>('video');

  const [playbackState, setPlaybackState] = useState<VideoPlaybackState>(() => initialPlaybackState(videoInfo.totalDuration));

  // Fetch manifest on mount
  useEffect(() => {
    const fetchManifest = async () => {
      try {
        setIsLoading(true);
        const { manifest: data, sessionToken: token } = await loadShareManifest(shareToken, manifestUrl);
        setSessionToken(token);
        setManifest(data);
        setIsLoading(false);
      } catch (err) {
        console.error('Error fetching manifest:', err);
        setError('Failed to load video');
        setIsLoading(false);
      }
    };

    fetchManifest();
  }, [manifestUrl, shareToken]);

  // Handle playback state changes from player
  const handleStateChange = useCallback((state: VideoPlaybackState) => {
    setPlaybackState(state);
    analytics.onStateChange(state);
  }, [analytics]);

  // Player control handlers
  // Tiered fullscreen delegated to the shared controller (iOS → drag-proof CSS
  // overlay; desktop/Android → native). onChange drives React state + analytics.
  const fsRef = useRef<ImmersiveFullscreen | null>(null);
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const fs = new ImmersiveFullscreen({
      container: el,
      isPlaying: () => playerRef.current?.getState().isPlaying ?? false,
      resume: () => playerRef.current?.play(),
      onChange: (active) => { setIsFullscreen(active); analytics.trackFullscreen(active); },
    });
    fsRef.current = fs;
    return () => { fs.destroy(); fsRef.current = null; };
  }, [analytics]);

  const enterFullscreen = useCallback(() => {
    if (!fsRef.current?.isActive()) fsRef.current?.toggle();
  }, []);

  // Auto-enter fullscreen on the FIRST play. Browsers only allow fullscreen from a user gesture,
  // and the play tap is that gesture. Once-only (don't fight the user if they exit mid-video) and
  // phone-only (desktop users don't expect a video to seize fullscreen on play).
  const autoFsAttempted = useRef(false);
  const handlePlayPause = useCallback(() => {
    const playing = playerRef.current?.getState().isPlaying ?? false;
    if (!playing && !autoFsAttempted.current && isTouchDevice) {
      autoFsAttempted.current = true;
      enterFullscreen();
    }
    playerRef.current?.togglePlay();
  }, [isTouchDevice, enterFullscreen]);

  const handlePlay = useCallback(() => {
    playerRef.current?.play();
  }, []);

  const handlePause = useCallback(() => {
    playerRef.current?.pause();
  }, []);

  const handleSeek = useCallback((time: number) => {
    // Preserve playing state through the seek (YouTube/Vimeo behavior).
    // Only force-pause when the user was already paused.
    const wasPlaying = playerRef.current?.getState().isPlaying ?? false;
    playerRef.current?.seek(time, { pauseAfterSeek: !wasPlaying });
  }, []);

  // Slide-granular timeline. Each slide is a segment whose width is proportional to its
  // duration. The controller's manifestUpdate carries authoritative startTime/endTime, so we
  // keep a fallback accumulation only for the brief window before it fires.
  const segments = useMemo(
    () =>
      (manifest?.slides ?? []).map((s) => ({
        id: s.id,
        title: s.title,
        duration: s.endTime != null && s.startTime != null
          ? s.endTime - s.startTime
          : (s.duration ?? 0),
      })),
    [manifest]
  );

  const slideStartTimes = useMemo(() => {
    const starts: number[] = [];
    let acc = 0;
    for (const s of manifest?.slides ?? []) {
      starts.push(s.startTime ?? acc);
      acc = s.endTime ?? acc + (s.duration ?? 0);
    }
    return starts;
  }, [manifest]);

  // Lightweight "loading slide…" indicator: jumping to a different slide makes the controller
  // fetch+render it. Show a small spinner until the next slide is up or 1.5s passes.
  const [isSlideBuffering, setIsSlideBuffering] = useState(false);
  const slideBufferTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Jump to a slide's START and keep playing if we were playing. Because the target time is the
  // slide boundary, the controller seeks to slideTime=0 — every slide begins from the beginning,
  // with no mid-slide seek or drift correction.
  const handleSlideJump = useCallback((index: number) => {
    if (!manifest || index < 0 || index >= manifest.slides.length) return;
    const state = playerRef.current?.getState();
    const wasPlaying = state?.isPlaying ?? false;
    if (typeof state?.currentSlideIndex === 'number' && state.currentSlideIndex !== index) {
      setIsSlideBuffering(true);
      if (slideBufferTimeoutRef.current) clearTimeout(slideBufferTimeoutRef.current);
      slideBufferTimeoutRef.current = setTimeout(() => setIsSlideBuffering(false), 1500);
    }
    playerRef.current?.seek(slideStartTimes[index] ?? 0, { pauseAfterSeek: !wasPlaying });
  }, [manifest, slideStartTimes]);

  // Clear the buffering indicator as soon as the new slide is up.
  useEffect(() => {
    if (isSlideBuffering) {
      setIsSlideBuffering(false);
      if (slideBufferTimeoutRef.current) {
        clearTimeout(slideBufferTimeoutRef.current);
        slideBufferTimeoutRef.current = null;
      }
    }
  }, [playbackState.currentSlideIndex]); // eslint-disable-line react-hooks/exhaustive-deps

  // Slides mode navigation handlers
  const handlePreviousSlide = useCallback(() => {
    if (!manifest || playbackState.currentSlideIndex <= 0) return;
    const prevSlideId = manifest.slides[playbackState.currentSlideIndex - 1].id;
    playerRef.current?.navigateSlideWithTransition(prevSlideId, 'prev');
  }, [manifest, playbackState.currentSlideIndex]);

  const handleNextSlide = useCallback(() => {
    if (!manifest || playbackState.currentSlideIndex >= manifest.slides.length - 1) return;
    const nextSlideId = manifest.slides[playbackState.currentSlideIndex + 1].id;
    playerRef.current?.navigateSlideWithTransition(nextSlideId, 'next');
  }, [manifest, playbackState.currentSlideIndex]);

  // Resume from visibility pause
  const handleVisibilityResume = useCallback(() => {
    playerRef.current?.play();
  }, []);

  // Scroll visibility detection for embedded/scrollable pages
  const { pausedByScroll, handleScrollResume, clearScrollPause } = useScrollVisibility(
    containerRef,
    playbackState.isPlaying,
    handlePause
  );

  // Combined resume handler for scroll pause overlay
  const handleScrollResumeWithPlay = useCallback(() => {
    handleScrollResume();
    playerRef.current?.play();
  }, [handleScrollResume]);

  // Clear scroll pause state when user plays via controls
  useEffect(() => {
    if (playbackState.isPlaying) {
      clearScrollPause();
    }
  }, [playbackState.isPlaying, clearScrollPause]);

  // Auto-hide drives only the TOP chrome (title/share). The bottom control bar is now
  // sticky (always visible), so there is no hidden "summon" gesture to learn.
  const autoHide = useAutoHideControls({ enabled: true });

  // Handle user activity from iframe / pointer.
  // The bottom controls are always visible; a single tap simply reveals the top chrome and
  // still passes through to interactive slide elements (the iframe handles the tap natively).
  // No double-tap, no dismiss-on-tap — the previous gesture was undiscoverable.
  const handleUserActivity = useCallback(() => {
    // On TOUCH devices, controls are summoned ONLY by tapping the bottom bar. We ignore all
    // activity-based summon here — including the synthetic mouse events (mousemove/mouseenter)
    // that a tap fires — so interacting with the slide never makes the panel reappear.
    // Desktop (mouse) still reveals controls on movement.
    if (isTouchDevice) return;
    autoHide.show();
  }, [autoHide, isTouchDevice]);

  // Swipe is a slides-mode (manual presentation) gesture. Slides mode is unplugged, and in
  // continuous video mode swiping must NOT change slides — navigation is via the segmented bar.
  // So gestures are disabled here. (handleNextSlide/handlePreviousSlide remain for slides mode.)
  const swipeHandlers = useSwipeGesture({
    onSwipeLeft: handleNextSlide,
    onSwipeRight: handlePreviousSlide,
    enabled: false,
  });

  // Scale calculation: playerAreaRef on desktop, containerRef on mobile (flat DOM)
  useEffect(() => {
    const updateScale = () => {
      const el = playerAreaRef.current || containerRef.current;
      if (el) {
        const rect = el.getBoundingClientRect();
        const scaleX = rect.width / resolution.width;
        const scaleY = rect.height / resolution.height;
        setScale(Math.min(scaleX, scaleY));
      }
    };

    updateScale();
    window.addEventListener('resize', updateScale);

    const resizeObserver = new ResizeObserver(updateScale);
    if (playerAreaRef.current) resizeObserver.observe(playerAreaRef.current);
    if (containerRef.current) resizeObserver.observe(containerRef.current);

    return () => {
      window.removeEventListener('resize', updateScale);
      resizeObserver.disconnect();
    };
  }, [resolution.width, resolution.height, compact]);

  // Fullscreen toggle — the controller decides tier and fires onChange.
  const handleFullscreen = useCallback(() => {
    fsRef.current?.toggle();
  }, []);

  // Keyboard shortcuts (desktop only). Skips events when an input/textarea/contenteditable is
  // focused so we don't hijack typing. Bound to document — works regardless of player focus.
  useEffect(() => {
    if (isTouchDevice) return;
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target) {
        const tag = target.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || target.isContentEditable) return;
      }
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      const key = e.key;
      if (key === ' ' || key === 'k' || key === 'K') {
        e.preventDefault();
        playerRef.current?.togglePlay();
        autoHide.show();
        return;
      }
      // Slide-granular navigation: arrows / j / l step between slides (each starts from its beginning).
      if (key === 'ArrowRight' || key === 'l' || key === 'L') {
        e.preventDefault();
        handleSlideJump(playbackState.currentSlideIndex + 1);
        autoHide.show();
        return;
      }
      if (key === 'ArrowLeft' || key === 'j' || key === 'J') {
        e.preventDefault();
        handleSlideJump(playbackState.currentSlideIndex - 1);
        autoHide.show();
        return;
      }
      if (key === 'f' || key === 'F') {
        e.preventDefault();
        handleFullscreen();
        return;
      }
      if (key === 'Home') {
        e.preventDefault();
        handleSlideJump(0);
        autoHide.show();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isTouchDevice, playbackState.currentSlideIndex, handleSlideJump, handleFullscreen, autoHide]);

  // Copy share URL
  const handleCopyLink = useCallback(async () => {
    try {
      analytics.trackShareClick();
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  }, []);

  const currentSlide = manifest?.slides?.[playbackState.currentSlideIndex];

  // Two-tier controls: the full control pill shows when summoned (recent activity) or while
  // paused; otherwise it collapses to the slim resting bar so it never covers content mid-play.
  const showFullControls = !autoHide.hidden || !playbackState.isPlaying;

  // Rotate nudge (mobile): when the device orientation doesn't match the video's native aspect,
  // suggest rotating. We never force it — the player refits to fill on rotation and the hint clears.
  const orientation = useViewportOrientation();
  const videoIsLandscape = resolution.width > resolution.height;
  const videoIsPortrait = resolution.height > resolution.width;
  const showRotateHint = isTouchDevice && !!manifest && !isLoading && !error &&
    ((videoIsLandscape && orientation === 'portrait') || (videoIsPortrait && orientation === 'landscape'));

  return (
    <div
      ref={containerRef}
      className={cn(
        'bg-black flex flex-col overflow-hidden relative',
        // 100dvh fills the *visible* viewport on mobile (accounts for the browser's address bar)
        // so the player is edge-to-edge immersive without relying on the Fullscreen API.
        isFullscreen ? 'fixed inset-0 z-[2147483647] w-screen h-[100dvh]' : 'h-[100dvh]'
      )}
    >
      {/* Desktop Header — hidden on compact or fullscreen */}
      {!compact && !isFullscreen && (
        <div className="flex-shrink-0 bg-black/80 backdrop-blur-sm py-3 px-6 border-b border-white/10">
          <div className="flex items-center justify-between max-w-7xl mx-auto gap-2">
            <h1 className="text-white font-medium truncate text-base min-w-0 flex-1">
              {videoInfo.title || 'Untitled Video'}
            </h1>
            <div className="flex items-center gap-3 flex-shrink-0">
              {/* Slides-mode toggle unplugged — see playerMode note above. */}
              <Button
                onClick={handleCopyLink}
                variant="ghost"
                size="sm"
                className="text-white/70 hover:text-white hover:bg-white/10"
              >
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                <span className="ml-2">{copied ? 'Copied!' : 'Share'}</span>
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Player area — flex-1 fills remaining space (full screen when no header/footer) */}
      <div
        ref={playerAreaRef}
        className={cn(
          "flex-1 relative overflow-hidden bg-black flex items-center justify-center",
          autoHide.hidden && 'cursor-none'
        )}
        {...swipeHandlers}
        onMouseMove={() => handleUserActivity()}
        onMouseLeave={() => autoHide.hideNow()}
      >
        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center">
            <div className="text-center text-white">
              <Loader2 className={cn("animate-spin mx-auto mb-3", compact ? "h-8 w-8" : "h-10 w-10")} />
              {!compact && <p className="text-sm opacity-80">Loading video...</p>}
            </div>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="flex items-center justify-center">
            <div className="text-center text-white">
              {!compact && <div className="text-4xl mb-4">⚠️</div>}
              <p className={cn(compact ? "text-sm text-white/60" : "text-lg font-medium")}>{error}</p>
            </div>
          </div>
        )}

        {/* Video Player */}
        {manifest && !isLoading && !error && (
          <>
            {/* Player wrapper — video + overlaid controls (stable tree position) */}
            <div
              className="relative"
              style={{
                width: `${resolution.width * scale}px`,
                height: `${resolution.height * scale}px`,
              }}
            >
              {/* Video with clipping */}
              <div className={cn(
                "absolute inset-0 bg-white overflow-hidden",
                !compact && "rounded-lg shadow-2xl"
              )}>
                <VideoCompositionPlayer
                  ref={playerRef}
                  manifest={manifest}
                  conversationId={shareToken}
                  authToken={sessionToken}
                  onStateChange={handleStateChange}
                  onManifestUpdate={setManifest}
                  onSlideStart={analytics.onSlideStart}
                  onVideoEnd={analytics.onVideoEnd}
                  onError={analytics.onError}
                  onUserActivity={handleUserActivity}
                  className="w-full h-full"
                />

                {/* Visibility/scroll pause overlay */}
                {(playbackState.pausedByVisibility || pausedByScroll) && (
                  <VisibilityPauseOverlay
                    onResume={pausedByScroll ? handleScrollResumeWithPlay : handleVisibilityResume}
                  />
                )}

                {/* Slide-jump buffering indicator — small, non-blocking */}
                {isSlideBuffering && (
                  <div className="absolute inset-0 pointer-events-none flex items-center justify-center z-20">
                    <div className="bg-black/60 backdrop-blur-sm rounded-full px-3 py-2 flex items-center gap-2">
                      <Loader2 className="h-4 w-4 text-white animate-spin" />
                      <span className="text-white/90 text-xs">Loading slide…</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Center play icon when paused — compact only */}
              {compact && !playbackState.isPlaying && (
                <div className="absolute inset-0 flex items-center justify-center z-10 pointer-events-none">
                  <button
                    type="button"
                    onClick={handlePlayPause}
                    aria-label="Play"
                    className="pointer-events-auto w-16 h-16 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center transition-transform hover:scale-110 active:scale-95"
                  >
                    <Play className="h-7 w-7 text-white ml-1 fill-current" />
                  </button>
                </div>
              )}

              {/* Slides Mode Controls */}
              {playerMode === 'slides' && (
                <SlidesModeControls
                  currentSlideIndex={playbackState.currentSlideIndex}
                  totalSlides={manifest.slides.length}
                  onPrevious={handlePreviousSlide}
                  onNext={handleNextSlide}
                  onFullscreen={handleFullscreen}
                  isFullscreen={isFullscreen}
                  currentSlideTitle={currentSlide?.title}
                  hidden={autoHide.hidden}
                  compact={compact}
                />
              )}
            </div>

            {/* Video controls — anchored to the PLAYER-AREA bottom (not the letterboxed video),
                so on tall/square videos they sit at the bottom edge instead of mid-screen. */}
            {playerMode === 'video' && (
              <>
                {/* Gradient scrim — only while full controls are summoned */}
                <div className={cn(
                  "absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-black/70 to-transparent pointer-events-none z-20 transition-opacity duration-300",
                  showFullControls ? "opacity-100" : "opacity-0"
                )} />

                {/* Slim resting bar — TAP to summon full controls. Content taps pass to the slide. */}
                <div
                  role="button"
                  tabIndex={showFullControls ? -1 : 0}
                  onClick={() => autoHide.show()}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); autoHide.show(); } }}
                  aria-label="Show player controls"
                  className={cn(
                    "absolute left-0 right-0 bottom-0 z-20 px-3 pt-4 pb-1.5 cursor-pointer transition-opacity duration-300",
                    showFullControls ? "opacity-0 pointer-events-none" : "opacity-100"
                  )}
                >
                  <SegmentedProgressBar
                    slim
                    segments={segments}
                    currentSlideIndex={playbackState.currentSlideIndex}
                    currentSlideTime={playbackState.currentSlideTime}
                    currentSlideDuration={playbackState.currentSlideDuration}
                    onSelect={handleSlideJump}
                  />
                </div>

                {/* Full controls — summoned, auto-collapse back to the slim bar */}
                <div className={cn(
                  "absolute left-0 right-0 z-30 transition-all duration-300",
                  compact ? "px-2 bottom-2" : "px-4 bottom-6",
                  showFullControls ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3 pointer-events-none"
                )}>
                  {compact ? (
                    <GsapPlayerControls
                      isPlaying={playbackState.isPlaying}
                      currentTime={playbackState.currentTime}
                      duration={playbackState.totalDuration}
                      playbackRate={playbackState.playbackRate}
                      isLoading={false}
                      onPlayPause={handlePlayPause}
                      onPlay={handlePlay}
                      onPause={handlePause}
                      onSeek={handleSeek}
                      segments={segments}
                      onSlideSelect={handleSlideJump}
                      currentSlideIndex={playbackState.currentSlideIndex}
                      currentSlideTime={playbackState.currentSlideTime}
                      currentSlideDuration={playbackState.currentSlideDuration}
                      onRestart={() => handleSlideJump(0)}
                      onStepBackward={() => handleSlideJump(playbackState.currentSlideIndex - 1)}
                      onStepForward={() => handleSlideJump(playbackState.currentSlideIndex + 1)}
                      onFullscreen={handleFullscreen}
                      compact
                    />
                  ) : (
                    <div className="max-w-3xl mx-auto">
                      <GsapPlayerControls
                        isPlaying={playbackState.isPlaying}
                        currentTime={playbackState.currentTime}
                        duration={playbackState.totalDuration}
                        playbackRate={playbackState.playbackRate}
                        isLoading={false}
                        onPlayPause={handlePlayPause}
                        onPlay={handlePlay}
                        onPause={handlePause}
                        onSeek={handleSeek}
                        segments={segments}
                        onSlideSelect={handleSlideJump}
                        currentSlideIndex={playbackState.currentSlideIndex}
                        currentSlideTime={playbackState.currentSlideTime}
                        currentSlideDuration={playbackState.currentSlideDuration}
                        onRestart={() => handleSlideJump(0)}
                        onStepBackward={() => handleSlideJump(playbackState.currentSlideIndex - 1)}
                        onStepForward={() => handleSlideJump(playbackState.currentSlideIndex + 1)}
                        onFullscreen={handleFullscreen}
                      />
                      <div className="text-center mt-2 text-white/60 text-sm">
                        Slide {playbackState.currentSlideIndex + 1} of {manifest.slides.length}
                        {currentSlide?.title && (
                          <span className="ml-2 text-white/80">— {currentSlide.title}</span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Rotate nudge — mobile, when device orientation ≠ video orientation */}
            {showRotateHint && <RotateDeviceHint target={videoIsLandscape ? 'landscape' : 'portrait'} />}

            {/* Desktop: Fullscreen-only exit button (mode toggle unplugged) */}
            {!compact && isFullscreen && (
              <Button
                onClick={handleFullscreen}
                variant="ghost"
                size="sm"
                className={cn(
                  "absolute top-3 left-3 z-30 text-white/70 hover:text-white hover:bg-white/10 transition-opacity duration-500",
                  autoHide.hidden ? "opacity-0 pointer-events-none" : "opacity-100"
                )}
              >
                <Minimize2 className="h-5 w-5" />
              </Button>
            )}
          </>
        )}
      </div>

      {/* Desktop Footer — hidden on compact or fullscreen */}
      {!compact && !isFullscreen && (
        <div className="flex-shrink-0 bg-black py-2 px-6 border-t border-white/10">
          <div className="flex items-center justify-between max-w-7xl mx-auto">
            <p className="text-white/40 text-xs">
              Powered by <span className="text-white/60">4K Labs</span>
            </p>
            {author && (
              <p className="text-white/40 text-xs">
                Created by <span className="text-white/60">{author}</span>
              </p>
            )}
          </div>
        </div>
      )}

      {/* Mobile floating overlays — positioned on the container */}
      {compact && manifest && !isLoading && !error && (
        <>
          {/* Floating title + share */}
          {!isFullscreen && (
            <div
              className={cn(
                "absolute top-0 left-0 right-0 z-30 transition-opacity duration-500",
                autoHide.hidden ? "opacity-0 pointer-events-none" : "opacity-100"
              )}
            >
              <div className="bg-gradient-to-b from-black/70 to-transparent px-3 pt-2 pb-8 pr-16">
                <div className="flex items-center gap-2">
                  <h1 className="text-white text-sm font-medium truncate min-w-0 flex-1">
                    {videoInfo.title || 'Untitled Video'}
                  </h1>
                  <Button
                    onClick={handleCopyLink}
                    variant="ghost"
                    size="sm"
                    className="text-white/70 hover:text-white hover:bg-white/10 flex-shrink-0 h-8 w-8 p-0"
                  >
                    {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Floating mode toggle unplugged — see playerMode note above. */}

          {/* Fullscreen Exit Button */}
          {isFullscreen && (
            <Button
              onClick={handleFullscreen}
              variant="ghost"
              size="sm"
              className={cn(
                "absolute top-3 left-3 z-30 text-white/70 hover:text-white hover:bg-white/10 transition-opacity duration-500",
                autoHide.hidden ? "opacity-0 pointer-events-none" : "opacity-100"
              )}
            >
              <Minimize2 className="h-5 w-5" />
            </Button>
          )}
        </>
      )}

      {/* Review mode overlay (?review=true) — only mounts when the share link */}
      {/* has allowReviews=true. Otherwise the param is ignored entirely.        */}
      {reviewMode && manifest && !isLoading && !error && (
        <ReviewModeOverlay
          shareToken={shareToken}
          manifest={manifest}
          playerRef={playerRef}
          playbackState={playbackState}
          videoTitle={videoInfo.title}
        />
      )}

      {/* Reviewer arrived with ?review=true but the creator has not enabled */}
      {/* reviews on this link — explain quietly so they don't think it broke. */}
      {reviewParam && !allowReviews && manifest && !isLoading && !error && (
        <div className="fixed top-3 left-1/2 -translate-x-1/2 z-30 px-3 py-1.5 rounded-full bg-zinc-900/85 backdrop-blur text-xs text-white/80 border border-white/10 pointer-events-none">
          Reviews are not enabled on this share link.
        </div>
      )}
    </div>
  );
}
